import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../src/layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_plugin_b9a5b3c2 from 'nuxt_plugin_plugin_b9a5b3c2' // Source: ./components/plugin.js (mode: 'all')
import nuxt_plugin_sentryserver_24d58279 from 'nuxt_plugin_sentryserver_24d58279' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_81c7de1e from 'nuxt_plugin_sentryclient_81c7de1e' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_plugin_50036872 from 'nuxt_plugin_plugin_50036872' // Source: ./composition-api/plugin.mjs (mode: 'all')
import nuxt_plugin_googlegtag_3ec29aa4 from 'nuxt_plugin_googlegtag_3ec29aa4' // Source: ./google-gtag.js (mode: 'client')
import nuxt_plugin_vuexpersistedstate_072cbd0a from 'nuxt_plugin_vuexpersistedstate_072cbd0a' // Source: ../src/plugins/vuex-persistedstate.ts (mode: 'client')
import nuxt_plugin_i18n_6a80ea94 from 'nuxt_plugin_i18n_6a80ea94' // Source: ../src/plugins/i18n (mode: 'all')
import nuxt_plugin_veevalidate_6e5ad03a from 'nuxt_plugin_veevalidate_6e5ad03a' // Source: ../src/plugins/vee-validate (mode: 'client')
import nuxt_plugin_stripe_3a68e97a from 'nuxt_plugin_stripe_3a68e97a' // Source: ../src/plugins/stripe (mode: 'client')
import nuxt_plugin_tooltip_ba49f866 from 'nuxt_plugin_tooltip_ba49f866' // Source: ../src/plugins/tooltip (mode: 'all')
import nuxt_plugin_services_4e38a4d9 from 'nuxt_plugin_services_4e38a4d9' // Source: ../src/plugins/services.ts (mode: 'all')
import nuxt_plugin_alert_535fa9c7 from 'nuxt_plugin_alert_535fa9c7' // Source: ../src/plugins/alert.ts (mode: 'all')
import nuxt_plugin_toast_bdf935c8 from 'nuxt_plugin_toast_bdf935c8' // Source: ../src/plugins/toast.ts (mode: 'all')
import nuxt_plugin_serviceupgradetips_17b6baf6 from 'nuxt_plugin_serviceupgradetips_17b6baf6' // Source: ../src/plugins/service-upgrade-tips.ts (mode: 'client')
import nuxt_plugin_walletsdk_3b26ed51 from 'nuxt_plugin_walletsdk_3b26ed51' // Source: ../src/plugins/wallet-sdk.ts (mode: 'client')
import nuxt_plugin_urlqueryparser_0a1ed756 from 'nuxt_plugin_urlqueryparser_0a1ed756' // Source: ../src/plugins/url-query-parser.ts (mode: 'client')
import nuxt_plugin_handleError_1735693a from 'nuxt_plugin_handleError_1735693a' // Source: ../src/plugins/handleError.ts (mode: 'client')
import nuxt_plugin_meta_fa7726ce from 'nuxt_plugin_meta_fa7726ce' // Source: ./composition-api/meta.mjs (mode: 'all')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root.$options.$nuxt
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":true,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext, config)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":".bit - Web3 identities for you and your community.","htmlAttrs":{"lang":"en"},"meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width,height=device-height,initial-scale=1.0,maximum-scale=1.0,user-scalable=no,viewport-fit=cover"},{"name":"apple-mobile-web-app-capable","content":"yes"},{"hid":"description","name":"description","content":".bit is a blockchain-based, decentralized and open sourced account system. It is the first account system with broad compatibility and complete ownership and control by users. .bit can be used as a cryptocurrency collection account, as a domain name, or as an account for accessing general Internet services."},{"hid":"keyword","name":"keywords","content":".bit, dotbit, did, .bit did, .bit domain, web3 domains, .bit web3, crypto domain, domain crypto, crypto domain name, web3 did, das, decentralized domain, buy domain with crypto, domain eth, domain bnb, domain sol, namedao, cross-chain did, bit das, .bit account, did id, nervos, ckb"},{"hid":"og:description","property":"og:description","content":".bit is a blockchain-based, decentralized and open sourced account system. It is the first account system with broad compatibility and complete ownership and control by users. .bit can be used as a cryptocurrency collection account, as a domain name, or as an account for accessing general Internet services."},{"hid":"og:title","property":"og:title","content":".bit - Web3 identities for you and your community."},{"hid":"og:site_name","property":"og:site_name","content":".bit - Web3 identities for you and your community."},{"hid":"og:type","property":"og:type","content":"article"},{"hid":"og:url","property":"og:url","content":"https:\u002F\u002Fdid.id\u002F"},{"hid":"og:image","property":"og:image","content":"https:\u002F\u002Fdata.did.id\u002Fimages\u002Fseo-thumbnail-image.png"},{"hid":"twitter:card","name":"twitter:card","content":"summary_large_image"},{"hid":"twitter:site","name":"twitter:site","content":".bit - Web3 identities for you and your community."},{"hid":"twitter:title","name":"twitter:title","content":".bit - Web3 identities for you and your community."},{"hid":"twitter:description","name":"twitter:description","content":".bit is a blockchain-based, decentralized and open sourced account system. It is the first account system with broad compatibility and complete ownership and control by users. .bit can be used as a cryptocurrency collection account, as a domain name, or as an account for accessing general Internet services."},{"hid":"twitter:image","name":"twitter:image","content":"https:\u002F\u002Fdata.did.id\u002Fimages\u002Fseo-thumbnail-image.png"},{"hid":"twitter:image:alt","name":"twitter:image:alt","content":".bit - Web3 identities for you and your community."}],"link":[{"rel":"icon","type":"image\u002Fx-icon","href":"\u002Ffavicon.ico"}],"script":[{"src":"\u002Fdata\u002Ficonpark.js?timestamp=1694083272881","async":true},{"src":"https:\u002F\u002Fwww.googletagmanager.com\u002Fgtag\u002Fjs?id=G-78S1V595M0","async":true}],"style":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (typeof nuxt_plugin_plugin_b9a5b3c2 === 'function') {
    await nuxt_plugin_plugin_b9a5b3c2(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_sentryserver_24d58279 === 'function') {
    await nuxt_plugin_sentryserver_24d58279(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_81c7de1e === 'function') {
    await nuxt_plugin_sentryclient_81c7de1e(app.context, inject)
  }

  if (typeof nuxt_plugin_plugin_50036872 === 'function') {
    await nuxt_plugin_plugin_50036872(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_googlegtag_3ec29aa4 === 'function') {
    await nuxt_plugin_googlegtag_3ec29aa4(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuexpersistedstate_072cbd0a === 'function') {
    await nuxt_plugin_vuexpersistedstate_072cbd0a(app.context, inject)
  }

  if (typeof nuxt_plugin_i18n_6a80ea94 === 'function') {
    await nuxt_plugin_i18n_6a80ea94(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_veevalidate_6e5ad03a === 'function') {
    await nuxt_plugin_veevalidate_6e5ad03a(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_stripe_3a68e97a === 'function') {
    await nuxt_plugin_stripe_3a68e97a(app.context, inject)
  }

  if (typeof nuxt_plugin_tooltip_ba49f866 === 'function') {
    await nuxt_plugin_tooltip_ba49f866(app.context, inject)
  }

  if (typeof nuxt_plugin_services_4e38a4d9 === 'function') {
    await nuxt_plugin_services_4e38a4d9(app.context, inject)
  }

  if (typeof nuxt_plugin_alert_535fa9c7 === 'function') {
    await nuxt_plugin_alert_535fa9c7(app.context, inject)
  }

  if (typeof nuxt_plugin_toast_bdf935c8 === 'function') {
    await nuxt_plugin_toast_bdf935c8(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_serviceupgradetips_17b6baf6 === 'function') {
    await nuxt_plugin_serviceupgradetips_17b6baf6(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_walletsdk_3b26ed51 === 'function') {
    await nuxt_plugin_walletsdk_3b26ed51(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_urlqueryparser_0a1ed756 === 'function') {
    await nuxt_plugin_urlqueryparser_0a1ed756(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_handleError_1735693a === 'function') {
    await nuxt_plugin_handleError_1735693a(app.context, inject)
  }

  if (typeof nuxt_plugin_meta_fa7726ce === 'function') {
    await nuxt_plugin_meta_fa7726ce(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
